import React, { forwardRef, RefObject, useEffect, useRef } from 'react';
import { arc as d3arc, pie as d3pie, sum as d3sum } from 'd3';

import { useTheme, useMessageFormatter } from '@redsift/design-system';
import intlMessages from './intl';

import { LegendVariant, ArcDatum, CategoryData, CategoryDatum, LegendItemDatum } from '../../types';
import { PieChartLegendVariant, PieChartProps, PieChartVariant } from './types';
import { StyledPieChartCenterText, StyledPieChart } from './styles';
import { Arcs } from '../Arcs';
import { useFormatCategoricalData } from '../../hooks';
import { Legend } from '../Legend';
import { sizeToDimension } from './utils';
import { PieChart } from './PieChart';
import { getSortingMethod, mergeLegends } from '../../utils';

interface RenderedPieChartProps extends Required<Pick<PieChartProps, 'data'>>, Omit<PieChartProps, 'data'> {}

export const RenderedPieChart = forwardRef<HTMLDivElement, RenderedPieChartProps>((props, ref) => {
  const {
    caping,
    className,
    data: propsData,
    disableAnimations,
    getSliceAnchorProps,
    id,
    isHalf,
    isSliceSelected,
    labelDecorator,
    legendDecorator,
    legendVariant,
    legendProps,
    middleText: propsMiddleText,
    onSliceClick,
    others,
    size,
    sliceProps,
    sliceRole,
    sortingMethod,
    subtext: propsSubtext,
    text: propsText,
    colorTheme,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tooltipDecorator: _tooltipDecorator,
    tooltipVariant,
    variant,
    ...forwardedProps
  } = props;

  const cache = useRef<CategoryData>();
  const theme = useTheme();

  const format = useMessageFormatter(intlMessages);

  const isDonut = variant === PieChartVariant.donut || variant === PieChartVariant.spacedDonut;
  const isSpaced = variant === PieChartVariant.spaced || variant === PieChartVariant.spacedDonut;

  const { data, colorScale } = useFormatCategoricalData({
    data: propsData,
    colorTheme: colorTheme!,
    sortingMethod: sortingMethod!,
    caping,
    others,
    theme,
  });

  useEffect(() => {
    cache.current = data;
  });

  const { width, height, smallFontSize, fontSize, innerRadius } = sizeToDimension(size!, isHalf);
  const externalRadiusPadding = 8;

  const createPie = d3pie<CategoryDatum>()
    .value((d) => d.value)
    .sort(null)
    .startAngle(isHalf ? Math.PI * -0.5 : 0)
    .endAngle(isHalf ? Math.PI * 0.5 : Math.PI * 2);
  const createArc = d3arc<ArcDatum>()
    .innerRadius(isDonut ? innerRadius : 0)
    .outerRadius(width / 2 - externalRadiusPadding);
  const pieData = createPie(data);
  const previousPieData = cache.current ? createPie(cache.current) : undefined;
  const total = d3sum(data, (d) => d.value);
  const legendWidth = useRef<string>();
  if (data && !legendWidth.current) {
    legendWidth.current = `${Math.max(...data.map((d) => d.key.length + String(d.value).length)) * 8 + 32}px`;
  }

  const text =
    typeof propsText === 'function'
      ? propsText(
          pieData.map((datum) => {
            const percent = datum.data.value / total;
            return { ...datum, data: { ...datum.data, percent } };
          }),
          total
        )
      : propsText;
  const subtext =
    typeof propsSubtext === 'function'
      ? propsSubtext(
          pieData.map((datum) => {
            const percent = datum.data.value / total;
            return { ...datum, data: { ...datum.data, percent } };
          }),
          total
        )
      : propsSubtext;
  const middleText =
    typeof propsMiddleText === 'function'
      ? propsMiddleText(
          pieData.map((datum) => {
            const percent = datum.data.value / total;
            return { ...datum, data: { ...datum.data, percent } };
          }),
          total
        )
      : propsMiddleText;

  const filteredPieData = pieData.filter((datum) => datum.data.value);

  const { extraLegendItems, ...forwardedLegendProps } = legendProps || {};

  return (
    <StyledPieChart
      mode={typeof onSliceClick === 'function' || typeof getSliceAnchorProps === 'function' ? 'interactive' : 'static'}
      definition={format('definition', { sliceLength: filteredPieData.length })}
      dataTableRepresentation={{
        header: (
          <thead>
            <tr>
              <th scope="col">Key</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
        ),
        body: (
          <tbody>
            {filteredPieData.map((datum) => (
              <tr key={datum.data.key}>
                <th scope="row">{datum.data.key}</th>
                <td>{datum.data.value}</td>
              </tr>
            ))}
          </tbody>
        ),
      }}
      {...forwardedProps}
      className={className}
      id={id}
      ref={ref as RefObject<HTMLDivElement>}
    >
      <div className={`${PieChart.className}__chart-wrapper`}>
        {text ? (
          <StyledPieChartCenterText
            $maxWidth={innerRadius * 2}
            $textSize={fontSize}
            $smallTextSize={smallFontSize}
            $theme={theme}
          >
            {subtext ? <b>{text}</b> : <span>{text}</span>}
            {middleText ? <b>{middleText}</b> : null}
            <span>{subtext}</span>
          </StyledPieChartCenterText>
        ) : null}
        <svg
          width={width}
          height={isHalf ? height + externalRadiusPadding : height}
          aria-label={
            typeof onSliceClick === 'function' || typeof getSliceAnchorProps === 'function'
              ? format('interactive-chart')
              : format('static-chart')
          }
          aria-hidden="false"
        >
          <Arcs
            arcs={filteredPieData.map((datum, index) => {
              const percent = datum.data.value / total;
              const from = previousPieData
                ? previousPieData[index]
                : {
                    data: {
                      key: '',
                      value: 0,
                    },
                    startAngle: isHalf ? Math.PI * -0.5 : 0,
                    endAngle: isHalf ? Math.PI * -0.5 : 0,
                    padAngle: 0,
                    value: 0,
                    index: index,
                  };
              const to = { ...datum, data: { ...datum.data, percent } };

              return {
                color: colorScale?.(to.data.key),
                createArc,
                previousData: from,
                id: `id${id}__arc-${index}`,
                index,
                isSelected: isSliceSelected!(to),
                key: `id${id}__arc-${index}`,
                data: to,
                dataset: pieData,
                anchorProps: getSliceAnchorProps ? getSliceAnchorProps(to) : undefined,
              };
            })}
            disableAnimations={disableAnimations}
            hasLabels={legendVariant === PieChartLegendVariant.internal}
            hasStroke={isSpaced}
            labelDecorator={labelDecorator}
            onClick={onSliceClick}
            role={sliceRole}
            sliceProps={sliceProps}
            tooltipVariant={tooltipVariant}
            transform={`translate(${width / 2} ${isHalf ? height : height / 2})`}
          />
        </svg>
      </div>
      {legendVariant !== PieChartLegendVariant.none && legendVariant !== PieChartLegendVariant.internal ? (
        <Legend
          data={(extraLegendItems
            ? mergeLegends(extraLegendItems, data).sort(getSortingMethod(sortingMethod!))
            : data
          ).map((d) => ({
            ...d,
            color: colorScale?.(d.key)!,
          }))}
          variant={
            legendVariant === PieChartLegendVariant.externalLabelValue
              ? LegendVariant.value
              : legendVariant === PieChartLegendVariant.externalLabelPercent
              ? LegendVariant.percent
              : legendVariant === PieChartLegendVariant.externalLabel
              ? LegendVariant.label
              : LegendVariant.custom
          }
          width={legendWidth.current}
          labelDecorator={
            labelDecorator ? (datum: LegendItemDatum, props) => labelDecorator(datum as ArcDatum, props) : undefined
          }
          legendDecorator={
            legendDecorator ? (datum: LegendItemDatum, props) => legendDecorator(datum as ArcDatum, props) : undefined
          }
          {...forwardedLegendProps}
        />
      ) : null}
    </StyledPieChart>
  );
});
